/**
 * 테넌트 식별 — apps/client/src/lib/api/client.ts 의 getSubdomain() 이식.
 *
 * 호스트에서 서브도메인을 뽑되, 뽑을 수 없는 환경(실기기 IP 접속, Cloud Run 기본 URL,
 * 서브도메인 없는 도메인)에서는 사용자가 입력한 회사 코드를 localStorage 에서 읽는다.
 */

/**
 * localStorage 키 접두사의 **앞부분** — 세계(DCMC/표준)를 가르는 몫이다 (2026-09-04).
 *
 * 🔴 전에는 호스트 앞부분에서 뽑았다 (`app.biznomi.com` → `biznomi_app`).
 *   경로 분리로 표준 제품이 `app.biznomi.com/mtt/` 에 얹히면서 **DCMC 의 Svelte 앱과
 *   같은 호스트·같은 origin** 을 쓰게 됐다. 그러면 양쪽 접두사가 똑같이
 *   `biznomi_app` 이 되어 **토큰이 서로 덮인다.**
 *
 *   apps/client 는 안 고친다 — 고치면 DCMC 2,600명이 로그아웃되고 Svelte 번들을
 *   배포하게 된다. 새 세계만 옮기면 충돌이 사라진다.
 *
 * ⚠ 호스트를 보지 않는다. 주소를 하나로 정했으므로 볼 이유가 없다
 *   (`vite.config.ts` 의 `base` 와 같은 이유). 호스트에서 뽑는 로직을 남겨 두면
 *   호스트가 하나 늘 때 또 갈린다.
 */
const BASE_PREFIX = 'biznomi_mtt'

/*
 * 🔴 접두사가 붙는다 (2026-09-04). 전에는 `biznomi_org_code` 로 접두사가 아예 없어서
 *   같은 origin 에 얹히면 **DCMC 와 회사 코드를 통째로 공유**했다. 한쪽에서 코드를
 *   바꾸면 다른 쪽이 그 회사를 보게 된다.
 */
const ORG_CODE_KEY = `${BASE_PREFIX}_org_code`

/**
 * 앱 자체의 호스트 — 테넌트 서브도메인이 아니다.
 *
 * 🔴 `mtt` 를 빠뜨렸었다 (2026-09-04 에 고침). `mtt.biznomi.com` 은 8/25 에 정한
 *   표준 제품 도메인인데 이 목록이 따라가지 않아서, 접속하면 회사 코드를 `mtt` 로
 *   읽고 **"조직을 찾을 수 없습니다"** 가 떴다.
 *   **prod 새 DB 에 조직이 하나도 없던 동안에는 안 드러났다** — 어느 코드로 읽든
 *   똑같이 못 찾았기 때문이다. 첫 조직(demo-hosp)을 만든 그날 드러났다.
 *
 * ⚠ 새 호스트를 만들면 여기에 넣는다. 안 넣으면 그 이름이 회사 코드가 된다.
 */
const RESERVED = ['app', 'admin', 'super', 'api', 'www', 'staging', 'client', 'landing', 'web', 'mtt']

function savedOrgCode(): string {
  if (typeof window === 'undefined') return ''
  return localStorage.getItem(ORG_CODE_KEY) ?? ''
}

export function getSubdomain(): string {
  const host = typeof window === 'undefined' ? '' : window.location.hostname

  // IP 직접 접속 (Android 실기기 테스트 등)
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return savedOrgCode()

  // xxx.localhost → xxx (로컬 개발)
  const local = host.match(/^([^.]+)\.localhost$/)
  if (local) return local[1]

  // Cloud Run 기본 URL 은 서브도메인이 아님
  if (host.endsWith('.run.app')) return savedOrgCode()

  // xxx.biznomi.com → xxx
  const parts = host.split('.')
  if (parts.length >= 3 && !RESERVED.includes(parts[0])) return parts[0]

  return savedOrgCode()
}

/**
 * 호스트가 테넌트를 정하는가.
 *
 * `dcmc.staging.biznomi.com` 이면 주소가 회사를 정하므로 회사 코드를 바꿀 수 없다
 * (바꿔 봐야 주소가 이긴다). `app.staging.biznomi.com` 처럼 예약 호스트이거나
 * 앱·IP 접속이면 저장된 코드가 회사를 정하므로 바꿀 수 있어야 한다.
 */
export function hostDecidesOrg(): boolean {
  const host = typeof window === 'undefined' ? '' : window.location.hostname
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return false
  if (host.endsWith('.run.app')) return false
  if (/^([^.]+)\.localhost$/.test(host)) return true
  const parts = host.split('.')
  return parts.length >= 3 && !RESERVED.includes(parts[0])
}

export function saveOrgCode(code: string) {
  localStorage.setItem(ORG_CODE_KEY, code)
}

/**
 * DCMC 화면이 넘겨 준 회사 코드를 받는다 (2026-09-07).
 *
 * 표준 제품 고객은 앱을 열면 DCMC 회사 코드 화면(`apps/client/routes/org-select`)을
 * 먼저 본다. 거기서 코드를 치면 옛 DB 에 없으니 못 찾고, 그 화면이 `/mtt/?org=<코드>`
 * 로 넘겨 준다.
 *
 * 🔴 **저장소를 통해 못 받는다.** 세계별로 키를 갈라 놨기 때문이다
 *   (저쪽 `biznomi_org_code` / 이쪽 `biznomi_mtt_org_code`). 저쪽에서 저장해 봐야
 *   이쪽은 못 읽고, 사용자가 같은 코드를 **두 번** 친다. 그래서 쿼리로 받는다.
 *
 * ⚠ 그리기 전에 불러야 한다. `getSubdomain()` 이 첫 렌더에서 이미 읽히고, 그때 비어
 *   있으면 회사 코드 화면이 한 번 깜빡였다가 사라진다. `main.tsx` 가 createRoot 앞에서 부른다.
 *
 * 주소에서는 지운다. 남겨 두면 이 회사 코드가 붙은 URL 이 히스토리·공유 링크로 돌아다닌다.
 */
export function adoptOrgCodeFromQuery(): void {
  if (typeof window === 'undefined') return

  const url = new URL(window.location.href)
  const raw = url.searchParams.get('org')
  if (raw === null) return

  const code = raw.trim().toLowerCase()
  // 서브도메인으로 쓰이는 값이라 모양이 정해져 있다. 아무 문자열이나 저장하지 않는다
  if (/^[a-z0-9][a-z0-9-]{0,62}$/.test(code)) saveOrgCode(code)

  url.searchParams.delete('org')
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

export function getSavedOrgCode(): string | null {
  return localStorage.getItem(ORG_CODE_KEY)
}

export function clearOrgCode() {
  localStorage.removeItem(ORG_CODE_KEY)
}

/**
 * 세션 키 접두사 — **회사까지 들어간다** (2026-09-09).
 *
 * 🔴 전에는 `biznomi_mtt` 하나였다. 같은 브라우저에서 회사를 바꾸면 앞 회사의 토큰
 *   자리에 뒤 회사 토큰이 덮였다. 탭을 둘 열고 각각 다른 회사로 로그인하면
 *   **나중 것이 이긴다** — 앞 탭은 자기 것이라고 믿는 토큰으로 남의 회사를 부른다.
 *   서버가 토큰과 `X-Subdomain` 을 대조해 403 을 내므로 데이터가 새지는 않았지만
 *   (8/28 대조), 멀쩡한 탭이 이유 없이 끊기는 건 그대로였다.
 *
 * ⚠ **회사 코드 자체(`ORG_CODE_KEY`)에는 이 접두사를 안 붙인다.** 그건 "지금 어느
 *   회사인가" 를 가리키는 포인터라, 회사를 알기 전에 읽어야 한다. 여기에 회사를
 *   넣으면 자기 자신을 알아야 자기를 읽는 꼴이 된다.
 *
 * ⚠ 토큰에는 순서 문제가 없다. 토큰은 로그인 **뒤에** 생기고 그때는 회사를 안다.
 *
 * 회사를 아직 모르면 앞부분만 쓴다. 그 상태에서 쓸 토큰도 없다.
 */
export function getStoragePrefix(): string {
  const org = getSubdomain().toLowerCase().replace(/[^a-z0-9-]/g, '')
  return org ? `${BASE_PREFIX}_${org}` : BASE_PREFIX
}

/** 옛 키를 옮길 때 쓴다 — 접두사에 회사가 없던 시절의 이름. */
export function getLegacyStoragePrefix(): string {
  return BASE_PREFIX
}
